<template>
  <div class="flex flex-col min-h-screen w-full font-primary antialiased" id="page-wrapper">
    <Header />

    <div class="grow bg-light-white">
      <slot />
    </div>

    <Footer />

    <!-- <FreshChatLoader /> -->

    <iframe class="absolute h-0 pointer-events-none" :src="iframeUrl" ref="iframe" />
    <SpeedInsights />
  </div>
</template>

<script setup lang="ts">
import Header from '~/components/Header.vue';
import Footer from '~/components/Footer.vue';
import type { TickslyPostMessageData } from '~/composables/useAuth';
import { useAuth } from '~/composables/useAuth';
import { computed, ref } from 'vue';
import { SpeedInsights } from '@vercel/speed-insights/nuxt';

const iframe = ref<HTMLIFrameElement | null>(null);
const auth = useAuth();

const iframeStamp = ref(0);
const iframeUrl = computed(() => {
  return `${auth.iframeEmbedUrl}?t=${iframeStamp.value}`;
});

// Handle postMessage auth response from ticksly iframe
const listener = (event: MessageEvent<TickslyPostMessageData | string>) => {
  if (event.data === 'MM:3PCunsupported') {
    console.log('Cookies not supported');
    return;
  } else if (event.data === 'MM:3PCsupported') {
    console.log('Cookies supported');
    return;
  }

  // Filter out messages not coming from ticksly iframe
  if (event.origin !== auth.iframeEmbedDomain) {
    return;
  }

  // Filter out messages not containing our object shape
  if (typeof event.data !== 'object') {
    return;
  }

  const user = event.data.user || {};
  if (!user?.id) {
    auth.clearSession();
    return;
  }
  auth.storeSession(user);
};

const requestGetSession = () => {
  if (!iframe.value) {
    return; // Skip until iframe is loaded
  }

  // Ask the iframe to send us user's session data
  // iframe.value.contentWindow?.addEventListener('message', innerListener);
  // https://developer.mozilla.org/en-US/docs/Web/API/Window/postMessage
  iframe.value.contentWindow?.postMessage('GetSession', '*');
};

// Force auth refresh when we come back to the page (in staging/production) using browser back
// If we signed out on tixly while editing profile and returning, this will help detect the change in session
// https://stackoverflow.com/a/43043658
const onPageShown = (event: PageTransitionEvent) => {
  const historyTraversal =
    (event as any).persisted ||
    (typeof window.performance != 'undefined' && window.performance.navigation.type === 2);

  if (historyTraversal) {
    iframeStamp.value = Math.round(Math.random() * 1000);
    // Trigger session check after iframe had a chance to render
    setTimeout(() => {
      requestGetSession();
    }, 1000);
  }
};

onMounted(() => {
  if (process.server) {
    return; // Skip when evaluated in SSR
  }

  // use random value to trigger iframe rerender on browser back to the page from tixly
  iframeStamp.value = Math.round(Math.random() * 1000);

  // Setup listener for messages received from ticksly iframe via window.postMessage
  window.addEventListener('message', listener);

  // Setup event listener for back navigation detection to retrigger auth
  window.addEventListener('pageshow', onPageShown);

  // Trigger session check with tixly
  requestGetSession();
});

onBeforeUnmount(() => {
  if (process.server) {
    return; // Skip when evaluated in SSR
  }

  // Cleanup listeners
  window.removeEventListener('message', listener);
  window.removeEventListener('pageshow', onPageShown);
});
</script>
